'use client';

import { localStorageHelper } from '@/lib/localStorageHelper';

/** Single localStorage entry for inspector post-process category/effect collapsibles (global, not per-project). */
export const POST_PROCESS_EXPANSION_STORAGE_KEY = 'genesys-postProcessExpansion';

/** Sparse: `c` holds only collapsed categories (false); `e` holds only expanded effects (true). */
type PostProcessExpansionState = {
  c?: Record<string, boolean>;
  e?: Record<string, boolean>;
};

const SAVE_DEBOUNCE_MS = 150;

let pendingSave: PostProcessExpansionState | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function readDisk(): PostProcessExpansionState {
  return localStorageHelper.getJson<PostProcessExpansionState>(POST_PROCESS_EXPANSION_STORAGE_KEY) ?? {};
}

function snapshot(): PostProcessExpansionState {
  const base = pendingSave ?? readDisk();
  return {
    ...(base.c && Object.keys(base.c).length > 0 ? { c: { ...base.c } } : {}),
    ...(base.e && Object.keys(base.e).length > 0 ? { e: { ...base.e } } : {}),
  };
}

function isStateEmpty(state: PostProcessExpansionState): boolean {
  const cEmpty = !state.c || Object.keys(state.c).length === 0;
  const eEmpty = !state.e || Object.keys(state.e).length === 0;
  return cEmpty && eEmpty;
}

function scheduleFlush(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const toWrite = pendingSave;
    pendingSave = null;
    if (!toWrite || isStateEmpty(toWrite)) {
      localStorageHelper.remove(POST_PROCESS_EXPANSION_STORAGE_KEY);
    } else {
      localStorageHelper.setJson(POST_PROCESS_EXPANSION_STORAGE_KEY, toWrite);
    }
  }, SAVE_DEBOUNCE_MS);
}

/** Category default in UI: expanded (`true`). Stored only when collapsed. */
export function getCategoryExpanded(categoryKey: string): boolean | undefined {
  const state = pendingSave ?? readDisk();
  if (state.c && Object.prototype.hasOwnProperty.call(state.c, categoryKey)) {
    return state.c[categoryKey];
  }
  return undefined;
}

/** Effect default in UI: collapsed (`false`). Stored only when expanded. */
export function getEffectExpanded(effectType: string): boolean | undefined {
  const state = pendingSave ?? readDisk();
  if (state.e && Object.prototype.hasOwnProperty.call(state.e, effectType)) {
    return state.e[effectType];
  }
  return undefined;
}

export function setCategoryExpanded(categoryKey: string, expanded: boolean): void {
  const next = snapshot();
  const c = { ...next.c };
  if (expanded) {
    delete c[categoryKey];
  } else {
    c[categoryKey] = false;
  }
  if (Object.keys(c).length > 0) {
    next.c = c;
  } else {
    delete next.c;
  }
  pendingSave = next;
  scheduleFlush();
}

export function setEffectExpanded(effectType: string, expanded: boolean): void {
  const next = snapshot();
  const e = { ...next.e };
  if (!expanded) {
    delete e[effectType];
  } else {
    e[effectType] = true;
  }
  if (Object.keys(e).length > 0) {
    next.e = e;
  } else {
    delete next.e;
  }
  pendingSave = next;
  scheduleFlush();
}

export function clearPostProcessExpansionStorage(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  pendingSave = null;
  localStorageHelper.remove(POST_PROCESS_EXPANSION_STORAGE_KEY);
}
